<script setup lang="ts">
import { api } from '@/api';
import {
  AppPermissionsEnum,
  RtSystemCategory,
  TaskItemDto,
  TaskTypeDescription,
} from '@/api/generator';
import PtwAddButton from '@/components/buttons/PtwAddButton.vue';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import { EntityInfo } from '@/components/confirmationModals/types';
import { TAB_PREFIX } from '@/components/confirmationModals/useFormConfirmationModal';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import { ContextMenuAction } from '@/components/ContextMenu/types';
import {
  getDefaultContextMenuActions,
  globalContextMenuHeaderItemDefinition,
} from '@/components/ContextMenu/utils';
import EntityChange from '@/components/EntityHistory/EntityChange.vue';
import FirstEntityFeedback from '@/components/PageFeedback/FirstEntityFeedback.vue';
import CommentCount from '@/components/forms/CommentCount/CommentCount.vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import { sectionTasks } from '@/components/icons/icons.ts';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import OverviewTableHeader from '@/components/OverviewTable/OverviewTableHeader.vue';
import { SortItem } from '@/components/OverviewTable/types.ts';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { routenames } from '@/router/routenames';
import { translateEntityProperty, truthy, getTranslatedOptions } from '@/utils/helper';
import {
  localizedEnumCompare,
  alphabeticalStringCompare,
  cascadedCompareFunction,
} from '@/utils/compareAndSort';
import { buildOdataQuery } from '@/utils/odata.ts';
import { I18nKey } from '@/utils/typings';
import { useLoading } from '@/utils/useLoading';
import { useTranslation } from 'i18next-vue';
import { computed, ref, watchEffect, useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';
import TaskOverviewFilter from './components/TaskOverviewFilter.vue';
import TaskTypeModal from './components/TaskTypeModal.vue';
import { TaskOverviewFilterObject, TaskTypeSelection } from './types';
import { useDeleteTaskWithConfirmation } from './utils';
import { getEntityChangeEvent } from '@/components/EntityHistory/entityChange';
import { useRemainingViewportHeight } from '@/utils/useRemainingVueport';
import { useTableScrollElement } from '@/utils/vuetifyMenuHelper';

const { t } = useTranslation();
const tableRef = useTemplateRef<HTMLDivElement>('table');
const { height } = useRemainingViewportHeight(tableRef);
const tableScrollElement = useTableScrollElement(tableRef);

const headers = [
  {
    title: t('Name'),
    key: 'name',
  },
  {
    title: t('Description'),
    key: 'description',
    width: '20%',
  },
  {
    title: t('StateOfUse'),
    key: 'state',
    sort: localizedEnumCompare('StateOfUseEnum'),
  },
  {
    // category column always has a category and a subCategory via business rule.
    title: `${t('Category_one')} | ${t('Type_one')}`,
    key: 'category',
    sortRaw: cascadedCompareFunction<TaskItemDto>([
      (a, b) => localizedEnumCompare('RtSystemCategoryEnum')(a.category ?? '', b.category ?? ''),
      (a, b) =>
        localizedEnumCompare('MedicalSystemSubCategoryEnum')(
          a.subCategory ?? '',
          b.subCategory ?? '',
        ),
    ]),
  },
  {
    title: t('TaskType_one'),
    key: 'taskType',
    sort: localizedEnumCompare('TaskTypeDescriptionEnum'),
  },
  {
    title: t('TargetArea_other'),
    key: 'taskItemAreas',
    sortRaw: (a: TaskItemDto, b: TaskItemDto) =>
      alphabeticalStringCompare(
        a.taskItemAreas?.map((area) => area.name).join('') ?? '',
        b.taskItemAreas?.map((area) => area.name).join('') ?? '',
      ),
  },

  {
    title: t('LastModification'),
    key: 'lastModificationTime',
  },

  globalContextMenuHeaderItemDefinition,
] as const;

const sortBy = ref<SortItem[]>([
  {
    key: 'name',
    order: 'asc',
  },
]);

const tasks = ref<TaskItemDto[]>();
const constTasks = ref<TaskItemDto[]>();

const router = useRouter();

const filterObject = ref<TaskOverviewFilterObject>({});

const categoryItems = computed(() => {
  const categories = constTasks.value
    ?.map((item) => item.category)
    .filter((item): item is RtSystemCategory => item !== undefined);

  const uniqueCategories = Array.from(new Set(categories));

  return uniqueCategories.map((item) => ({
    title: t(`RtSystemCategoryEnum.${item}` as I18nKey),
    value: item,
  }));
});

const targetAreaNames = computed<Array<string>>(() => {
  const names = new Set();
  tasks.value?.forEach((task) => {
    task.taskItemAreas?.forEach((item) => {
      names.add(item.name);
    });
  });
  return [...names] as Array<string>;
});

const taskTypeItems = ref<Array<{ title: string; value: TaskTypeDescription }> | undefined>();

function getTaskItemAreasFilter(filter: Array<string>) {
  return {
    or: [
      ...filter.map((entry) => ({
        taskItemAreas: {
          any: {
            name: entry,
          },
        },
      })),
    ],
  };
}

const {
  action: loadTasks,
  loading,
  loadingFirstTime,
} = useLoading(async () => {
  const { result } = await api.taskItemsApi.getTaskItemList(
    buildOdataQuery({
      search: filterObject.value?.search,
      filter: {
        ...(filterObject.value?.state?.length && {
          state: { in: filterObject.value?.state },
        }),

        ...(filterObject.value?.taskType?.length && {
          taskType: { eq: filterObject.value?.taskType },
        }),

        ...(filterObject.value?.category?.length && {
          category: { in: filterObject.value?.category },
        }),

        and: [filterObject.value.taskItemAreas].filter(truthy).map(getTaskItemAreasFilter),
      },
    }),
  );
  tasks.value = result?.value || undefined;
  if (constTasks.value === undefined && tasks.value?.length) {
    constTasks.value = [...tasks.value];
  }
  if (!taskTypeItems.value) {
    const tmpArray = getTranslatedOptions(
      Object.values(TaskTypeDescription),
      'TaskTypeDescriptionEnum',
    );

    taskTypeItems.value = tmpArray.filter((item) =>
      tasks?.value?.some((elem) => item.value === elem.taskType),
    );
  }
}, true);

const { action: deleteAction } = useDeleteTaskWithConfirmation();

// Really true? what if filter active and still no results
const showFirstEntityFeedback = computed(() => {
  return (
    !loading.value && tasks.value?.length === 0 && Object.keys(filterObject.value).length === 0
  );
});

function getContextMenuActions(item: TaskItemDto): ContextMenuAction[] {
  const defaultContextMenuActions = getDefaultContextMenuActions(item);
  return [
    {
      ...defaultContextMenuActions.view,
      to: {
        name: routenames.tasks.details,
        params: { id: item.id },
        hash: `#${TAB_PREFIX}configuration`,
      },
    },

    {
      ...defaultContextMenuActions.edit,
      to: {
        name: routenames.tasks.edit,
        params: { id: item.id },
        hash: `#${TAB_PREFIX}configuration`,
      },
    },
    {
      ...defaultContextMenuActions.delete,
      callback: async () => {
        const defaultConfirmationOptions = getDefaultDeleteConfirmationOptions(
          {
            ...(item as EntityInfo),
            typeName: 'Task_one',
          },
          t,
        );
        const { result } = await deleteAction({
          param: item.id,
          confirmationOptions: {
            ...defaultConfirmationOptions,
            size: 'large',
            contentData: {
              ...defaultConfirmationOptions.contentData,
              hint: t('DeleteConfigurationInfoMsg', {
                hint: defaultConfirmationOptions.contentData.hint,
              }),
            },
          },
        });
        if (result?.success) {
          constTasks.value = undefined;
          taskTypeItems.value = undefined;
          loadTasks();
        }
      },
    },
  ];
}

function navigateTo(taskId: string, hash?: string) {
  router.push({
    name: routenames.tasks.details,
    hash,
    params: { id: taskId },
  });
}
watchEffect(loadTasks);

const chooseTaskDialogOpen = ref(false);

function toggleTaskDialog() {
  chooseTaskDialogOpen.value = !chooseTaskDialogOpen.value;
}

function dialogHandler(taskTypeSelection: TaskTypeSelection) {
  router.push({
    name: routenames.tasks.add,
    params: { ...taskTypeSelection },
  });
}
</script>

<template>
  <TaskTypeModal v-model="chooseTaskDialogOpen" @submit="dialogHandler" />

  <BasePage :loading="loadingFirstTime">
    <template #title>
      <BasePageTitle :title="$t('MedicalSystemTasks')" />
    </template>

    <template #actions>
      <PtwAddButton v-if="tasks?.length" @click="toggleTaskDialog()" />
    </template>

    <template v-if="!showFirstEntityFeedback">
      <TaskOverviewFilter
        v-model="filterObject"
        :categoryItems="categoryItems"
        :targetAreaNames="targetAreaNames"
        :taskTypeItems="taskTypeItems"
      />

      <OverviewTable
        ref="table"
        class="mt-2"
        :headers="headers"
        :items="tasks"
        :loading="loading"
        :itemLabel="'MedicalSystemTask'"
        :sortBy="sortBy"
        data-testid="DataTable_MedicalSystem"
        :height="height"
        fixedHeader
        @click:row="
          (_event, data) => {
            navigateTo(data.item.id, `#${TAB_PREFIX}configuration`);
          }
        "
      >
        <template #header.name="headerProps">
          <OverviewTableHeader v-bind="headerProps">
            {{ $t('Name') }}
            <span class="font-weight-medium">| {{ $t('ID') }}</span>
          </OverviewTableHeader>
        </template>
        <template #header.category="headerProps">
          <OverviewTableHeader v-bind="headerProps">
            {{ $t('Category_one') }}&nbsp;| {{ $t('Type_one') }}
          </OverviewTableHeader>
        </template>

        <template #item.name="{ item }">
          <IdentifierLabel :label="translateEntityProperty('name', item)" :identifier="item.iuid" />
          <CommentCount :value="item" @click.stop="navigateTo(item.id, `#${TAB_PREFIX}comments`)" />
        </template>

        <template #item.contextMenu="{ item }">
          <ContextMenu
            :actions="getContextMenuActions(item)"
            :closeOnScrollElements="tableScrollElement"
          />
        </template>

        <template #item.category="{ item }">
          {{ $t(`RtSystemCategoryEnum.${item.category}` as I18nKey) }}&nbsp;|
          {{ $t(`MedicalSystemSubCategoryEnum.${item.subCategory}` as I18nKey) }}
        </template>

        <template #item.taskType="{ item }">
          {{ $t((`TaskTypeDescriptionEnum.${item.taskType}` as I18nKey) || '') }}
        </template>

        <template #item.state="{ item }">
          {{ $t(`StateOfUseEnum.${item.state}` as I18nKey) }}
          <EntityChange
            :entityChange="getEntityChangeEvent(item, 'editing')"
            :showEmptyFieldSign="false"
          />
        </template>

        <template #item.taskItemAreas="{ item }">
          <template v-if="item.taskItemAreas && item.taskItemAreas.length > 0">
            <p
              v-for="myArea in item.taskItemAreas"
              :key="myArea.id"
              :style="{ whiteSpace: 'nowrap' }"
            >
              {{ myArea.name }}
            </p>
          </template>
          <template v-else>
            {{ EMPTY_FIELD_SIGN }}
          </template>
        </template>

        <template #item.lastModificationTime="{ item }">
          <EntityChange :entityChange="getEntityChangeEvent(item, 'modified')" />
        </template>
      </OverviewTable>
    </template>
    <FirstEntityFeedback
      v-if="showFirstEntityFeedback"
      :permission="AppPermissionsEnum.GlobalTaskManagementAdd"
      :noPermissionMessage="$t('NoTasksConfigured')"
      :icon="sectionTasks"
      :actionText="$t('AddMedicalSystemTaskEntity')"
      @action="toggleTaskDialog()"
    />
  </BasePage>
</template>
